import { Context, Data, Effect, Layer, Option } from 'effect';

import { ErrorTag } from '../../data/error';
import { ContextTag } from '../context';

export class RefreshLock extends Context.Service<RefreshLock>()(ContextTag.RefreshLock, {
  make: Effect.gen(function* () {
    yield* Effect.addFinalizer(() => Effect.logDebug('[RefreshLock] Finalized'));

    const isNavigatorLocksAvailable = typeof navigator !== 'undefined' && 'locks' in navigator;

    const withLock = <A, E, R>(
      lockName: string,
      effect: Effect.Effect<A, E, R>,
      options: { timeout?: number } = {}
    ) =>
      Effect.gen(function* () {
        if (!isNavigatorLocksAvailable) {
          yield* Effect.logInfo('Navigator.locks not available, executing without cross-tab lock');
          return yield* effect;
        }

        const runtime = yield* Effect.context<R>();

        return yield* Effect.tryPromise({
          try: async (): Promise<A> =>
            await navigator.locks.request(
              lockName,
              {
                mode: 'exclusive' as const,
                ifAvailable: false,
                ...(options.timeout ? { signal: AbortSignal.timeout(options.timeout) } : {}),
              },
              async (lock): Promise<A> => {
                if (!lock) {
                  throw new LockAcquisitionError({ lockName, cause: 'Lock not acquired' });
                }

                await Effect.runPromiseWith(runtime)(Effect.logDebug('Lock acquired'));

                const result = await Effect.runPromiseWith(runtime)(effect);

                await Effect.runPromiseWith(runtime)(Effect.logDebug('Lock released'));

                return result;
              }
            ),
          catch: (error) => {
            if (error instanceof Error && error.name === 'AbortError') {
              Effect.runSyncWith(runtime)(
                Effect.logError('Lock timeout').pipe(
                  Effect.annotateLogs({ timeout: options.timeout })
                )
              );
              return new LockTimeoutError({ lockName, timeout: options.timeout ?? 0 });
            }

            if (error instanceof LockAcquisitionError || error instanceof LockTimeoutError) {
              return error;
            }

            Effect.runSyncWith(runtime)(
              Effect.logError('Lock acquisition failed').pipe(Effect.annotateLogs({ error }))
            );

            return new LockAcquisitionError({ lockName, cause: error });
          },
        });
      }).pipe(
        Effect.withLogSpan('withLock'),
        Effect.annotateLogs({
          lockName,
          timeout: options.timeout,
          mode: 'exclusive',
          ifAvailable: false,
        })
      );

    const tryLock = <A, E, R>(lockName: string, effect: Effect.Effect<A, E, R>) =>
      Effect.gen(function* () {
        if (!isNavigatorLocksAvailable) {
          yield* Effect.logInfo('Navigator.locks not available, executing without lock');
          const result = yield* effect;
          return Option.some<A>(result);
        }

        const runtime = yield* Effect.context<R>();

        return yield* Effect.tryPromise({
          try: async (): Promise<Option.Option<A>> =>
            await navigator.locks.request(
              lockName,
              {
                mode: 'exclusive' as const,
                ifAvailable: true,
              },
              async (lock): Promise<Option.Option<A>> => {
                const runPromise = Effect.runPromiseWith(runtime);

                if (!lock) {
                  await runPromise(Effect.logDebug('Lock not available (held by another tab)'));
                  return Option.none<A>();
                }

                await runPromise(Effect.logDebug('Lock acquired'));

                const result = await runPromise(effect);

                await runPromise(Effect.logDebug('Lock released'));

                return Option.some<A>(result);
              }
            ),
          catch: (error) => {
            Effect.runSyncWith(runtime)(
              Effect.logError('Lock acquisition failed').pipe(Effect.annotateLogs({ error }))
            );
            return new LockAcquisitionError({ lockName, cause: error });
          },
        });
      }).pipe(
        Effect.withLogSpan('tryLock'),
        Effect.annotateLogs({ lockName, mode: 'exclusive', ifAvailable: true })
      );

    yield* Effect.logDebug('[RefreshLock] initialized').pipe(
      Effect.annotateLogs({ service: 'RefreshLock', isNavigatorLocksAvailable })
    );

    return {
      withLock,
      tryLock,
      isNavigatorLocksAvailable,
    };
  }),
}) {
  static readonly layer = Layer.effect(this, this.make);
}

export class LockTimeoutError extends Data.TaggedError(ErrorTag.LockTimeoutError)<{
  readonly lockName: string;
  readonly timeout: number;
}> {}

export class LockAcquisitionError extends Data.TaggedError(ErrorTag.LockAcquisitionError)<{
  readonly lockName: string;
  readonly cause: unknown;
}> {}
